import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../client.js';

const KEY = (pid: string) => ['projects', pid, 'stakeholders'] as const;
const MATRIX_KEY = (pid: string) => ['projects', pid, 'stakeholder-matrix'] as const;

export function useStakeholders(projectId: string | undefined) {
  return useQuery({
    queryKey: KEY(projectId ?? ''),
    queryFn: () => apiClient.get(`/api/v1/projects/${projectId}/stakeholders`),
    enabled: !!projectId,
  });
}

export function useStakeholderMatrix(projectId: string | undefined) {
  return useQuery({
    queryKey: MATRIX_KEY(projectId ?? ''),
    queryFn: () => apiClient.get(`/api/v1/projects/${projectId}/stakeholder-matrix`),
    enabled: !!projectId,
  });
}

export function useCreateStakeholder(pid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Record<string, unknown>) => apiClient.post(`/api/v1/projects/${pid}/stakeholders`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY(pid) });
      qc.invalidateQueries({ queryKey: MATRIX_KEY(pid) });
    },
  });
}

export function useUpdateStakeholder(pid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      apiClient.patch(`/api/v1/projects/${pid}/stakeholders/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY(pid) });
      qc.invalidateQueries({ queryKey: MATRIX_KEY(pid) });
    },
  });
}

export function useDeleteStakeholder(pid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`/api/v1/projects/${pid}/stakeholders/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY(pid) });
      qc.invalidateQueries({ queryKey: MATRIX_KEY(pid) });
    },
  });
}
